import { AlertTriangle, Check } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";

type SegmentFooterProps = {
  isDirty: boolean;
  isSaving: boolean;
  affectedCount: number;
  onCancel: () => void;
  secondaryAction?: ReactNode;
};

const FOOTER_TRANSITION = { type: "spring", bounce: 0, duration: 0.3 } as const;

export function SegmentFooter({
  isDirty,
  isSaving,
  affectedCount,
  onCancel,
  secondaryAction,
}: SegmentFooterProps) {
  const { t } = useTranslation();
  const saveRef = useTourTarget<HTMLButtonElement>(SEGMENT_TOUR_TARGET.SAVE);

  return (
    <motion.div
      data-sticky-footer
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={FOOTER_TRANSITION}
      className="sticky bottom-0 z-10 flex items-center gap-3 bg-background py-3"
    >
      <Button type="button" variant="outline" onClick={onCancel}>
        {t("common.cancel")}
      </Button>
      {secondaryAction}
      <span
        aria-live="polite"
        className="min-w-0 flex-1 text-center text-sm text-muted-foreground [overflow-wrap:anywhere]"
      >
        {isDirty &&
          affectedCount === 0 &&
          t("admin.segments.builder.unsavedChanges")}
        {affectedCount > 0 && (
          <span className="inline-flex items-center gap-2 font-medium [color:var(--badge-warning-text)]">
            <AlertTriangle aria-hidden className="size-4 shrink-0" />
            {t("admin.segments.builder.exceptions.affectedFooter", {
              count: affectedCount,
            })}
          </span>
        )}
      </span>
      <Button ref={saveRef} type="submit" loading={isSaving}>
        <Check />
        {t("admin.segments.builder.save")}
      </Button>
    </motion.div>
  );
}
